import { useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Ban, Edit, RotateCcw, Undo2 } from "lucide-react";

import { Badge } from "@components/badge/badge";
import { Button } from "@components/button/button";
import { Spinner } from "@components/spinner/spinner";
import { DynamicTable } from "@components/table-custom/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@components/tabs";

import { CreateModeloModal } from "@pages/admin/gerenciar-modelos-tecnicos/components/add-new-model-modal/add-new-model-modal";
import { EditModeloModal } from "@pages/admin/gerenciar-modelos-tecnicos/components/edit-model-modal/edit-model-modal";
import { ModalInativarModelo } from "@pages/admin/gerenciar-modelos-tecnicos/components/inativar-modelo-modal";
import { ModalReativarModelo } from "@pages/admin/gerenciar-modelos-tecnicos/components/reativar-modelo-modal";
import { SearchInputFixaTechnical } from "@pages/admin/gerenciar-modelos-tecnicos/components/search-fixa-technical";

import { useModelosPorCliente } from "@/hooks/modelos/buscar-modelos-por-clientes";

import { useStoreZustandModelos } from "@/store-zustand/modelos/modelos";

import { FEATURES, useFeature } from "@/utils/auth/features";
import { handleLimitTextdisplayByAmount } from "@/utils/text-limit";

// BAMAQ NEW HOLLAND - Eduardo Magalhães
const dataTitle = [
  {
    accessor: "nome_modelo",
    label: "Ficha técnica",
    // destaque do modelo inativo fica junto ao nome para aparecer também no mobile
    customRender: (value: string, item: { ativo: boolean }) => (
      <div className="flex flex-wrap items-center gap-2">
        <span>{handleLimitTextdisplayByAmount({ text: value, limit: 120 })}</span>
        {!item.ativo && (
          <Badge className="border-transparent bg-red-100 text-red-700 hover:bg-red-100">
            Inativo
          </Badge>
        )}
      </div>
    ),
  },
  { accessor: "foto_frente_path", label: "Foto frente", isImage: true },
  { accessor: "foto_verso_path", label: "Foto verso", isImage: true },
  { accessor: "qtd_numeros_variaveis", label: "Número de campos", limit: 5 },
];

type IDataEdit = {
  id: number;
  ativo: boolean;
  cliente: string;
  produto: string;
  nomeModelo: string;
  tipoEntrega: string;
  temFuro: string;
  posicionamento: string;
  temCargaFoto: boolean;
  temDadosVariaveis: boolean;
  campChave: string;
  observacoes: string;
  camposVariaveis: [{ id: number; nome: string; obrigatorio: boolean }];
  qtdNumerosVariaveis: number;
  fotoFrenteUrl: string;
  fotoVersoUrl: string;
  modeloCompleto: {
    id: number;
    cliente: string;
    produto: string;
    nomeModelo: string;
    tipoEntrega: string;
    temFuro: string;
    posicionamento: string;
    temCargaFoto: boolean;
    temDadosVariaveis: boolean;
    campChave: string;
    observacoes: string;
    camposVariaveis: [{ id: number; nome: string; obrigatorio: boolean }];
    qtdNumerosVariaveis: number;
    fotoFrenteUrl: string;
    fotoVersoUrl: string;
  };
};

/**
 * Tela para listar as fixas tecnicas
 */
const ManageTechnicalSheets = () => {
  const location = useLocation();
  const { userCliente } = location.state || {};

  const navigate = useNavigate();
  const podeCriarModelo = useFeature(FEATURES.CRIAR_MODELOS_TECNICOS);
  const podeEditarModelo = useFeature(FEATURES.EDITAR_MODELOS_TECNICOS);
  const podeInativarModelo = useFeature(FEATURES.INATIVAR_MODELOS_TECNICOS);

  const {
    currentPage,
    setCurrentPage,
    totalItemsPage,
    isLoadingPage,
    lastPage,
    isModalInativarModelo,
    setModeloUnico,
    modeloUnico,
    setIsModalInativarModelo,
    isModalReativarModelo,
    setIsModalReativarModelo,
  } = useStoreZustandModelos();

  const {
    modelosPorClientes,
    isLoading: isLoadingModeloPages,
    paginations,
  } = useModelosPorCliente(userCliente || 0, { incluirInativos: true });
  const [isModal, setIsModal] = useState(false);
  const [isModalEdit, setIsModalEdit] = useState(false);

  const onGoToPage = useCallback(
    (action: number | "prev" | "next") => {
      const newPage =
        action === "prev"
          ? Math.max(1, currentPage - 1)
          : action === "next"
            ? currentPage + 1
            : action;

      setCurrentPage(newPage);
    },
    [currentPage, setCurrentPage]
  );

  const handleGoBack = () => {
    navigate(-1); // Volta uma página no histórico
  };

  return (
    <>
      {isLoadingPage ? (
        <Spinner />
      ) : (
        <>
          <Tabs defaultValue="all">
            <div className="flex items-center">
              <TabsList>
                <TabsTrigger value="all">Ficha Técnica</TabsTrigger>
              </TabsList>
              <div className="mb-4 ml-auto mr-4 flex items-center gap-2">
                <Button
                  size="sm"
                  variant="default"
                  className="h-8 gap-1 text-neutral-50"
                  onClick={handleGoBack}
                >
                  <Undo2 className="h-3.5 w-3.5" />
                  <span className="sr-only text-neutral-50 sm:not-sr-only sm:whitespace-nowrap">
                    Voltar
                  </span>
                </Button>

                {podeCriarModelo && (
                  <Button
                    size="sm"
                    variant="default"
                    className="h-8 gap-1 text-neutral-50"
                    onClick={() => setIsModal(!isModal)}
                  >
                    <span className="sr-only text-neutral-50 sm:not-sr-only sm:whitespace-nowrap">
                      Adicionar modelo
                    </span>
                  </Button>
                )}
              </div>
            </div>

            <div className="mx-auto w-full rounded-lg bg-white p-6 shadow-md dark:bg-white">
              <h2 className="dark:text-gray text-xl font-semibold text-gray-800">
                Gerenciar ficha tecnica
              </h2>
              <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
                Faça todo gerenciamento de suas fichas técnicas.
              </p>

              <TabsContent value="all">
                <DynamicTable
                  title="Fichas Técnicas"
                  description="Gerencie suas fichas e visualize quando quiser."
                  isLoadingPage={isLoadingModeloPages}
                  columns={dataTitle}
                  data={modelosPorClientes ?? []}
                  currentPage={currentPage}
                  itemsPerPage={totalItemsPage}
                  totalItems={totalItemsPage}
                  lastPage={lastPage}
                  onGoToPage={onGoToPage}
                  paginations={paginations}
                  rowClassName={(item: IDataEdit) =>
                    !item.ativo && "bg-gray-100/80 text-gray-500 [&_img]:grayscale"
                  }
                  actions={[
                    {
                      icon: Edit,
                      label: "Editar",
                      textColor: "text-red-600",
                      visible: podeEditarModelo,
                      onClick: (item: IDataEdit) => {
                        setModeloUnico(item?.modeloCompleto as any);
                        setIsModalEdit(true);
                      },
                    },
                    {
                      icon: Ban,
                      label: "Inativar modelo",
                      textColor: "text-red-600",
                      visible: (item: IDataEdit) => podeInativarModelo && item.ativo,
                      onClick: (item: IDataEdit) => {
                        setModeloUnico(item?.modeloCompleto as any);
                        setIsModalInativarModelo(true);
                      },
                    },
                    {
                      // inativos só chegam na listagem para admin/produção/consultor (filtro no backend)
                      icon: RotateCcw,
                      label: "Reativar modelo",
                      iconClassName: "h-4 w-4 text-green-600",
                      visible: (item: IDataEdit) => podeInativarModelo && !item.ativo,
                      onClick: (item: IDataEdit) => {
                        setModeloUnico(item?.modeloCompleto as any);
                        setIsModalReativarModelo(true);
                      },
                    },
                  ]}
                  childerSearch={<SearchInputFixaTechnical />}
                />
              </TabsContent>
            </div>
          </Tabs>

          {podeCriarModelo && !!isModal && (
            <CreateModeloModal
              handleToggleModal={() => {
                setIsModal(!isModal);
              }}
            />
          )}

          {podeEditarModelo && isModalEdit && modeloUnico && (
            <EditModeloModal
              handleToggleModal={() => {
                setIsModalEdit(false);
              }}
              modelo={modeloUnico as any}
            />
          )}

          {podeInativarModelo && isModalInativarModelo && (
            <ModalInativarModelo onCancel={() => setIsModalInativarModelo(false)} />
          )}

          {podeInativarModelo && isModalReativarModelo && (
            <ModalReativarModelo onCancel={() => setIsModalReativarModelo(false)} />
          )}
        </>
      )}
    </>
  );
};

export { ManageTechnicalSheets };
